import React, { useState, useEffect } from 'react'
import { TrendingUp, TrendingDown, Award, Target, AlertCircle } from 'lucide-react'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { apiClient } from '../utils/api'

interface TrendData {
  date: string
  completed: number
  verified: number
  points: number
}

interface ChildPerformance {
  child_id: number
  child_name: string
  completion_rate: number
  avg_points_per_day: number
  streak_days: number
  trend: 'up' | 'down' | 'stable'
}

const PerformanceTrends: React.FC = () => {
  const [trendData, setTrendData] = useState<TrendData[]>([])
  const [childPerformance, setChildPerformance] = useState<ChildPerformance[]>([])
  const [loading, setLoading] = useState(true)
  const [range, setRange] = useState('30d')

  // Mock data for development
  const mockTrendData: TrendData[] = [
    { date: 'Aug 1', completed: 12, verified: 10, points: 85 },
    { date: 'Aug 2', completed: 15, verified: 14, points: 110 },
    { date: 'Aug 3', completed: 8, verified: 7, points: 55 },
    { date: 'Aug 4', completed: 14, verified: 13, points: 95 },
    { date: 'Aug 5', completed: 16, verified: 15, points: 120 },
    { date: 'Aug 6', completed: 11, verified: 10, points: 75 },
    { date: 'Aug 7', completed: 13, verified: 12, points: 90 },
  ]

  const mockChildPerformance: ChildPerformance[] = [
    {
      child_id: 1,
      child_name: 'Emma',
      completion_rate: 92,
      avg_points_per_day: 28,
      streak_days: 7,
      trend: 'up'
    },
    {
      child_id: 2,
      child_name: 'Noah',
      completion_rate: 88,
      avg_points_per_day: 25,
      streak_days: 5,
      trend: 'stable'
    },
    {
      child_id: 3,
      child_name: 'Sophia',
      completion_rate: 75,
      avg_points_per_day: 20,
      streak_days: 3,
      trend: 'down'
    },
    {
      child_id: 4,
      child_name: 'Lucas',
      completion_rate: 95,
      avg_points_per_day: 30,
      streak_days: 10,
      trend: 'up'
    }
  ]

  useEffect(() => {
    fetchTrends()
  }, [range])

  const fetchTrends = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get(`/api/reports/performance-trends?range=${range}`)

      const trends = response.trends || []
      const childPerf = response.childPerformance || response.child_performance || []

      if (trends.length > 0) {
        setTrendData(trends)

        // Map child performance to expected format
        const mappedChildPerf = childPerf.map((child: any) => ({
          child_id: child.child_id || child.id || 0,
          child_name: child.child_name || child.name || '',
          completion_rate: typeof child.completion_rate === 'number'
            ? child.completion_rate
            : parseInt(child.completion || child.completion_rate || '0'),
          avg_points_per_day: child.avg_points_per_day || child.avgPointsPerDay || 0,
          streak_days: typeof child.streak_days === 'number'
            ? child.streak_days
            : parseInt(child.streak?.replace(' days', '') || '0'),
          trend: child.trend || 'stable'
        }))

        setChildPerformance(mappedChildPerf)
      } else {
        // Use mock data if API doesn't return expected format
        setTrendData(mockTrendData)
        setChildPerformance(mockChildPerformance)
      }
    } catch (error) {
      console.log('API not available, using mock data')
      setTrendData(mockTrendData)
      setChildPerformance(mockChildPerformance)
    } finally {
      setLoading(false)
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />
      default:
        return <div className="w-4 h-4 bg-gray-400 rounded-full" />
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-100 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Performance Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Performance Trends</h2>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
        
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="completed" 
              stroke="#3b82f6" 
              name="Completed"
              strokeWidth={2}
            />
            <Line 
              type="monotone" 
              dataKey="verified" 
              stroke="#10b981" 
              name="Verified"
              strokeWidth={2}
            />
            <Line 
              type="monotone" 
              dataKey="points" 
              stroke="#f59e0b" 
              name="Points"
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Child Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {childPerformance.map((child) => (
          <div key={child.child_id} className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">{child.child_name}</h3>
              {getTrendIcon(child.trend)}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Completion</span>
                <span className="font-medium">{child.completion_rate}%</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Avg Points/Day</span>
                <span className="font-medium">{child.avg_points_per_day}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Streak</span>
                <span className="font-medium flex items-center gap-1">
                  {child.streak_days} days
                  {child.streak_days >= 7 && <Award className="w-3 h-3 text-yellow-500" />}
                </span>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${child.completion_rate}%` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Performance Insights */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">Performance Insights</h3>
            <p className="text-sm text-blue-700">
              Overall completion rate is trending up by 15% this week.
              Lucas has the longest streak at 10 days!
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PerformanceTrends
